import { useState, useMemo } from 'react';
import { Plus, Trash2, CheckCircle, ChevronDown, Info } from 'react-feather';
import { DillModal, DillButton, DillToggle } from '../../../general/Dill';

// ─────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────

const RULE_TYPES = {
  PARAM: 'param',
  ACTIVE: 'active',
};

type Param = {
  key: string;
  label: string;
  description: string;
  userLabel: string; // plain-English label for end users
  type: 'toggle' | 'select' | 'number';
  options?: string[];
  optional?: boolean;
  placeholder?: string;
};

type Example = {
  goal: string;
  [key: string]: string;
};

type CategoryKey = keyof typeof CATEGORIES;

type Rule = {
  id: number;
  name: string;
  subtitle: string;
  userDescription: string; // plain English "You'll be notified when..."
  category: CategoryKey;
  type: string;
  documented: boolean;
  description: string;
  params: Param[];
  examples: Example[];
  notes: string[];
};

type AddedRule = {
  rule: Rule;
  paramValues: Record<string, string | number | boolean>;
  isActive: boolean;
};

// ─────────────────────────────────────────────
// DATA
// ─────────────────────────────────────────────

const CATEGORIES = {
  credit_score: { label: 'Credit Score', color: 'blue', icon: '📊' },
  credit_limit: { label: 'Credit Limit', color: 'indigo', icon: '💳' },
  company_info: { label: 'Company Info', color: 'slate', icon: '🏢' },
  financials: { label: 'Financials', color: 'green', icon: '📑' },
  payment: { label: 'Payment Behaviour', color: 'cyan', icon: '💰' },
  risk: { label: 'Risk Events', color: 'orange', icon: '⚠️' },
  bankruptcy: { label: 'Bankruptcy & Legal', color: 'red', icon: '⚖️' },
} as const;

const COLOR_MAP = {
  blue: {
    badge: 'bg-blue-50 text-blue-700 border-blue-200',
    dot: 'bg-blue-500',
    soft: 'bg-blue-50',
    text: 'text-blue-700',
  },
  indigo: {
    badge: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    dot: 'bg-indigo-500',
    soft: 'bg-indigo-50',
    text: 'text-indigo-700',
  },
  slate: {
    badge: 'bg-slate-100 text-slate-600 border-slate-200',
    dot: 'bg-slate-400',
    soft: 'bg-slate-50',
    text: 'text-slate-600',
  },
  green: {
    badge: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    dot: 'bg-emerald-500',
    soft: 'bg-emerald-50',
    text: 'text-emerald-700',
  },
  cyan: {
    badge: 'bg-cyan-50 text-cyan-700 border-cyan-200',
    dot: 'bg-cyan-500',
    soft: 'bg-cyan-50',
    text: 'text-cyan-700',
  },
  orange: {
    badge: 'bg-orange-50 text-orange-700 border-orange-200',
    dot: 'bg-orange-500',
    soft: 'bg-orange-50',
    text: 'text-orange-700',
  },
  red: {
    badge: 'bg-red-50 text-red-700 border-red-200',
    dot: 'bg-red-500',
    soft: 'bg-red-50',
    text: 'text-red-700',
  },
  amber: {
    badge: 'bg-amber-50 text-amber-700 border-amber-200',
    dot: 'bg-amber-500',
    soft: 'bg-amber-50',
    text: 'text-amber-700',
  },
};

type ColorType = keyof typeof COLOR_MAP;

const ALL_RULES: Rule[] = [
  {
    id: 101,
    name: 'International Score Band Change',
    subtitle: 'A–E Band Monitoring',
    category: 'credit_score',
    type: RULE_TYPES.PARAM,
    documented: true,
    userDescription:
      "Get notified when a company's international credit band (A–E) moves up or down beyond the threshold you set.",
    description:
      "Notifies when a company's international score band (A–E) drops or changes based on configured thresholds.",
    params: [
      {
        key: 'param0',
        label: 'Band Drop Count',
        userLabel: 'Alert me if the band drops by this many levels',
        type: 'select',
        optional: true,
        options: ['', '1', '2', '3', '4'],
        description: 'Leave empty if you only want to use the threshold band below.',
      },
      {
        key: 'param1',
        label: 'Threshold Band',
        userLabel: 'Alert me if the band reaches this level or worse',
        type: 'select',
        optional: true,
        options: ['', 'A', 'B', 'C', 'D', 'Any'],
        description:
          "Choose 'Any' to catch both improvements and declines. Choose a specific band (e.g. C) to only be alerted when the score drops to that level or below.",
      },
      {
        key: 'param2',
        label: 'Include new band in alert',
        userLabel: 'Show the new band in my alert email',
        type: 'toggle',
        optional: true,
        description: 'When on, the updated band (e.g. "C") will be included in the notification.',
      },
    ],
    examples: [
      { param0: '""', param1: 'Any', param2: 'true', goal: 'Alert on any score change' },
      { param0: '1', param1: '""', param2: 'true', goal: 'Alert if band drops by 1 level' },
      { param0: '""', param1: 'B', param2: 'false', goal: 'Alert if score drops below B' },
      { param0: '2', param1: '""', param2: 'true', goal: 'Alert if band drops by 2+ levels' },
    ],
    notes: [
      "Setting 'Any' for the threshold overrides the drop count and catches both improvements and declines.",
      'By default this rule only watches for declines — not improvements.',
      "There's no option to monitor score increases only.",
    ],
  },
  {
    id: 102,
    name: 'Credit Limit Change',
    subtitle: 'Any Change or Threshold Drop',
    category: 'credit_limit',
    type: RULE_TYPES.PARAM,
    documented: true,
    userDescription:
      "Get notified when a company's recommended credit limit moves — either any change at all, or only when it drops below a threshold you define.",
    description:
      'Triggers on credit limit changes — either any movement, or based on % drop / absolute value thresholds.',
    params: [
      {
        key: 'param0',
        label: 'Alert on any change',
        userLabel: 'Notify me about any credit limit movement (up or down)',
        type: 'toggle',
        optional: false,
        description:
          'When on, you will be alerted every time the credit limit changes, regardless of direction or size. The thresholds below are ignored.',
      },
      {
        key: 'param1',
        label: 'Minimum % drop to alert',
        userLabel: 'Only alert me if the limit drops by at least this %',
        type: 'number',
        optional: true,
        placeholder: 'e.g. 10',
        description:
          'Only applies when the option above is off. Enter a percentage, e.g. 10 means alert if limit drops by 10% or more.',
      },
      {
        key: 'param2',
        label: 'Alert if limit falls below this value',
        userLabel: 'Alert me if the credit limit drops below this amount',
        type: 'number',
        optional: true,
        placeholder: 'e.g. 1000',
        description:
          'Only applies when the option above is off. Enter an absolute amount — you will be alerted whenever the limit drops below this figure.',
      },
    ],
    examples: [
      { param0: 'true', param1: '–', param2: '–', goal: 'Alert on any credit limit change' },
      {
        param0: 'false',
        param1: '10',
        param2: '1000',
        goal: 'Alert if limit drops 10% or falls below 1000',
      },
      {
        param0: 'false',
        param1: '20',
        param2: '500',
        goal: 'Alert if limit drops 20% or falls below 500',
      },
      { param0: 'false', param1: '(none)', param2: '(none)', goal: 'No alerts will be sent' },
    ],
    notes: [
      "If 'Alert on any change' is on, the percentage and value thresholds are ignored entirely.",
      'The percentage threshold only fires on decreases, not increases.',
      'The value threshold is an absolute floor — not a change amount.',
    ],
  },
  {
    id: 110,
    name: 'International Credit Score % Change',
    subtitle: 'Percentage-Based Score Threshold',
    category: 'credit_score',
    type: RULE_TYPES.PARAM,
    documented: false,
    userDescription:
      "Get notified when a company's international credit score moves by a percentage you choose — in either direction.",
    description:
      'Triggers a notification when the international credit score changes by a configured percentage in either direction.',
    params: [
      {
        key: 'param0',
        label: '% change to trigger alert',
        userLabel: 'Alert me if the score changes by at least this %',
        type: 'number',
        optional: false,
        placeholder: 'e.g. 20',
        description:
          'Enter a percentage. You will be alerted whenever the credit score moves up or down by at least this amount.',
      },
    ],
    examples: [{ param0: '20', goal: 'Alert if score moves by 20% or more' }],
    notes: ['This rule fires on both increases and decreases.'],
  },
  {
    id: 103,
    name: 'Company Name Change',
    subtitle: 'Legal Entity Name Update',
    category: 'company_info',
    type: RULE_TYPES.ACTIVE,
    documented: false,
    userDescription: "Get notified when a company's legal registered name is updated.",
    description:
      'Triggers a notification when the legal registered name of a monitored company changes.',
    params: [],
    examples: [],
    notes: ['No configuration needed — just turn it on to start receiving alerts.'],
  },
  {
    id: 104,
    name: 'Registered Address Change',
    subtitle: 'Headquarters / Reg. Office',
    category: 'company_info',
    type: RULE_TYPES.ACTIVE,
    documented: false,
    userDescription:
      'Get notified when a company moves or updates its official registered address.',
    description: "Notifies when a company's registered (legal) address is updated in the registry.",
    params: [],
    examples: [],
    notes: ['No configuration needed — just turn it on to start receiving alerts.'],
  },
  {
    id: 105,
    name: 'Company Status Change',
    subtitle: 'Active / Inactive / Dissolved',
    category: 'risk',
    type: RULE_TYPES.ACTIVE,
    documented: false,
    userDescription:
      "Get notified if a company's trading status changes — including if it becomes inactive, is dissolved, or comes back into operation.",
    description:
      "Alerts on any change to the company's operative status — including dissolution, insolvency, or re-activation.",
    params: [],
    examples: [],
    notes: [
      'Covers changes to: Inactive, Dissolved, Insolvent, Bankrupt, and back to Active.',
      'Recommended for all monitored companies.',
    ],
  },
  {
    id: 106,
    name: 'Court Judgment Filed',
    subtitle: 'CCJ or Equivalent Legal Action',
    category: 'risk',
    type: RULE_TYPES.ACTIVE,
    documented: false,
    userDescription:
      'Get notified when a court judgment or legal order is registered against a company — such as a County Court Judgment (CCJ).',
    description:
      'Triggers when a new County Court Judgment (CCJ) or equivalent legal event is registered against the company.',
    params: [],
    examples: [],
    notes: [
      'Covers global court judgments and legal orders.',
      'Some countries have additional dedicated rules for specific filing types.',
    ],
  },
  {
    id: 107,
    name: 'Director or Officer Change',
    subtitle: 'Management & Governance',
    category: 'company_info',
    type: RULE_TYPES.ACTIVE,
    documented: false,
    userDescription:
      'Get notified when a company appoints or loses a director, officer, or key person in management.',
    description:
      'Notifies when directors, officers, or key management personnel are appointed or resign.',
    params: [],
    examples: [],
    notes: ['Useful for tracking governance changes and management risk.'],
  },
  {
    id: 108,
    name: 'Annual Accounts Filed',
    subtitle: 'Financial Filing Event',
    category: 'financials',
    type: RULE_TYPES.ACTIVE,
    documented: false,
    userDescription:
      'Get notified when a company submits new annual accounts or financial statements.',
    description:
      'Alerts when a company files new annual accounts or financial statements with the relevant authority.',
    params: [],
    examples: [],
    notes: ['Filing availability varies by country and company type.'],
  },
  {
    id: 109,
    name: 'Industry Code Change',
    subtitle: 'Business Classification (SIC)',
    category: 'company_info',
    type: RULE_TYPES.ACTIVE,
    documented: false,
    userDescription:
      "Get notified if a company's industry classification (SIC code) changes, which may signal a shift in their core business.",
    description:
      'Triggers when the Standard Industrial Classification (SIC) or equivalent industry code changes for the company.',
    params: [],
    examples: [],
    notes: [
      'A change in industry code can signal that a company has pivoted or changed its primary business.',
    ],
  },
  {
    id: 111,
    name: 'Insolvency or Administration Filing',
    subtitle: 'Winding-Up or Administration Notice',
    category: 'bankruptcy',
    type: RULE_TYPES.ACTIVE,
    documented: false,
    userDescription:
      'Get an immediate alert if a company enters insolvency, administration, or begins winding up proceedings.',
    description:
      'Notifies when an insolvency, winding-up, or administration filing is detected for the company.',
    params: [],
    examples: [],
    notes: [
      'This is a high-priority risk signal — recommended for all counterparties.',
      'Fires as soon as a filing is detected.',
    ],
  },
  // US Rules
  {
    id: 1601,
    name: 'Credit Rating Change',
    subtitle: 'US Companies — Increase or Decrease',
    category: 'credit_score',
    type: RULE_TYPES.PARAM,
    documented: true,
    userDescription:
      "Get notified when a US company's credit rating moves up or down past the threshold you set.",
    description:
      'Monitors rating changes for US companies. Triggers when a rating increases or decreases beyond set thresholds.',
    params: [
      {
        key: 'param0',
        label: 'Minimum % change',
        userLabel: 'Alert me if the rating changes by at least this %',
        type: 'number',
        optional: false,
        placeholder: 'e.g. 10',
        description:
          'You will be alerted if the rating moves up or down by at least this percentage.',
      },
      {
        key: 'param1',
        label: 'Minimum score change',
        userLabel: 'Or alert me if the score moves by at least this many points',
        type: 'number',
        optional: false,
        placeholder: 'e.g. 20',
        description:
          'An alternative threshold — you will be alerted if the raw score changes by this many points or more, in either direction.',
      },
    ],
    examples: [
      {
        param0: '10',
        param1: '20',
        goal: 'Alert if rating changes by 10% or by more than 20 points',
      },
    ],
    notes: ['Either condition being met will trigger the alert — you do not need both to fire.'],
  },
  {
    id: 1602,
    name: 'Company Name Change',
    subtitle: 'US Legal Entity Name Update',
    category: 'company_info',
    type: RULE_TYPES.ACTIVE,
    documented: false,
    userDescription:
      "Get notified when a US company's legal name is updated in state or federal records.",
    description:
      "Triggers a notification when the US company's legal name is updated in state or federal records.",
    params: [],
    examples: [],
    notes: ['No configuration needed — just turn it on.'],
  },
  {
    id: 1603,
    name: 'Company Address Change',
    subtitle: 'US Principal Office / Mailing Address',
    category: 'company_info',
    type: RULE_TYPES.ACTIVE,
    documented: false,
    userDescription:
      'Get notified when a US company moves or changes its principal business address.',
    description: "Notifies when the US company's registered or principal business address changes.",
    params: [],
    examples: [],
    notes: ['No configuration needed — just turn it on.'],
  },
  {
    id: 1604,
    name: 'Days Beyond Terms (DBT) Change',
    subtitle: 'Payment Timeliness Indicator',
    category: 'payment',
    type: RULE_TYPES.PARAM,
    documented: true,
    userDescription:
      "Get notified when a US company's average payment delay (Days Beyond Terms) changes significantly — an early warning sign of cash flow problems.",
    description:
      'Monitors changes in DBT (Days Beyond Terms) — the average number of days past due on payments.',
    params: [
      {
        key: 'param0',
        label: 'Minimum % change in payment delays',
        userLabel: 'Alert me if payment delays change by at least this %',
        type: 'number',
        optional: false,
        placeholder: 'e.g. 10',
        description:
          'You will be alerted if the DBT value moves up or down by at least this percentage.',
      },
      {
        key: 'param1',
        label: 'Minimum change in days',
        userLabel: 'Or alert me if payment delays change by at least this many days',
        type: 'number',
        optional: false,
        placeholder: 'e.g. 5',
        description:
          'An alternative threshold — alert if the DBT value changes by this many days or more.',
      },
    ],
    examples: [
      { param0: '10', param1: '5', goal: 'Alert if DBT changes by 10% or by more than 5 days' },
    ],
    notes: [
      'DBT rising means a company is taking longer to pay — a potential early warning sign.',
      'Either threshold being met will trigger the alert.',
    ],
  },
  {
    id: 1605,
    name: 'Company Status Change',
    subtitle: 'US Active / Inactive / Dissolved',
    category: 'risk',
    type: RULE_TYPES.ACTIVE,
    documented: false,
    userDescription:
      "Get notified if a US company's operating status changes — including state-level revocations or dissolution.",
    description: "Alerts on any change to the US company's operative status.",
    params: [],
    examples: [],
    notes: ['Covers state-level status transitions including revocation and dissolution.'],
  },
  {
    id: 1606,
    name: 'Number of Adverse Filings Changed',
    subtitle: 'Judgments, Liens & Bankruptcies Count',
    category: 'risk',
    type: RULE_TYPES.PARAM,
    documented: true,
    userDescription:
      'Get notified when the total number of adverse filings against a US company (court judgments, tax liens, bankruptcies) increases or decreases.',
    description:
      'Monitors changes in the total count of derogatory filings (judgments, liens, bankruptcies) for US companies.',
    params: [
      {
        key: 'param0',
        label: '% change in filing count',
        userLabel: 'Alert me if the number of filings changes by at least this %',
        type: 'number',
        optional: false,
        placeholder: 'e.g. 10',
        description:
          'Alert if the total count of adverse filings increases or decreases by this percentage or more.',
      },
      {
        key: 'param1',
        label: 'Minimum change in number of filings',
        userLabel: 'Or alert me if the filing count changes by at least this many',
        type: 'number',
        optional: false,
        placeholder: 'e.g. 3',
        description:
          'An alternative threshold — alert if the count of filings changes by this number or more.',
      },
    ],
    examples: [
      {
        param0: '10',
        param1: '3',
        goal: 'Alert if filing count changes by 10% or by 3 or more filings',
      },
    ],
    notes: [
      'Either threshold being met will trigger the alert.',
      'A rising count is a strong signal of increasing legal or financial risk.',
    ],
  },
  {
    id: 1607,
    name: 'Total Outstanding Adverse Filing Amount Changed',
    subtitle: 'Total Value of Judgments & Liens',
    category: 'risk',
    type: RULE_TYPES.PARAM,
    documented: true,
    userDescription:
      'Get notified when the total dollar amount owed across all adverse filings against a US company changes significantly.',
    description:
      'Monitors changes in the total outstanding dollar amount across all derogatory filings.',
    params: [
      {
        key: 'param0',
        label: '% change in outstanding amount',
        userLabel: 'Alert me if the total owed amount changes by at least this %',
        type: 'number',
        optional: false,
        placeholder: 'e.g. 10',
        description: 'Alert if the outstanding total changes by this percentage or more.',
      },
      {
        key: 'param1',
        label: 'Minimum dollar change ($)',
        userLabel: 'Or alert me if the total owed amount changes by at least this $',
        type: 'number',
        optional: false,
        placeholder: 'e.g. 5000',
        description:
          'An alternative threshold — alert if the outstanding amount changes by this value or more.',
      },
    ],
    examples: [
      {
        param0: '10',
        param1: '5000',
        goal: 'Alert if outstanding amount changes by 10% or by more than $5,000',
      },
    ],
    notes: [
      'Pair with Rule 1606 to monitor both how many filings exist and how much is at stake.',
      'Either threshold being met will trigger the alert.',
    ],
  },
  {
    id: 1608,
    name: 'Bankruptcy Filed',
    subtitle: 'Chapter 7, 11 or 13 Petition',
    category: 'bankruptcy',
    type: RULE_TYPES.ACTIVE,
    documented: false,
    userDescription:
      'Get an immediate alert if a US company files for bankruptcy — under any chapter including liquidation (Ch. 7), restructuring (Ch. 11), or repayment plan (Ch. 13).',
    description:
      'Triggers when a US bankruptcy petition is filed — any chapter including Chapter 7 (liquidation), Chapter 11 (reorganization), or Chapter 13.',
    params: [],
    examples: [],
    notes: [
      'Sourced from US Bankruptcy Court (PACER) records.',
      'Highly recommended for all US counterparties.',
    ],
  },
  {
    id: 1609,
    name: 'Court Judgment Filed',
    subtitle: 'US Civil or Federal Court Action',
    category: 'bankruptcy',
    type: RULE_TYPES.ACTIVE,
    documented: false,
    userDescription:
      'Get notified when a new court judgment or civil legal action is filed against a US company.',
    description:
      'Notifies when a new court judgment or civil action is filed against the US company.',
    params: [],
    examples: [],
    notes: ['Covers both state and federal civil court judgments.'],
  },
  {
    id: 1610,
    name: 'Tax Lien Filed',
    subtitle: 'IRS or State Tax Authority',
    category: 'bankruptcy',
    type: RULE_TYPES.ACTIVE,
    documented: false,
    userDescription:
      'Get notified when the IRS or a state tax authority files a lien against a US company for unpaid taxes.',
    description:
      'Alerts when a federal or state tax lien is filed against the company for unpaid tax obligations.',
    params: [],
    examples: [],
    notes: ['Covers both IRS (federal) and state-level tax liens.'],
  },
  {
    id: 1611,
    name: 'Secured Debt (UCC) Filing Changed',
    subtitle: 'Uniform Commercial Code Update',
    category: 'risk',
    type: RULE_TYPES.ACTIVE,
    documented: false,
    userDescription:
      "Get notified when a US company's secured debt filings (UCC) change — which may indicate they have taken on new collateral-backed obligations.",
    description:
      'Monitors Uniform Commercial Code (UCC) filing changes, indicating changes in secured debt positions.',
    params: [],
    examples: [],
    notes: ['New UCC filings may indicate increased leverage or financial pressure.'],
  },
  {
    id: 1612,
    name: 'Industry Code Change',
    subtitle: 'US SIC Classification',
    category: 'company_info',
    type: RULE_TYPES.ACTIVE,
    documented: false,
    userDescription:
      "Get notified if a US company's industry classification changes — which may indicate they have shifted their core business.",
    description:
      'Triggers when the Standard Industrial Classification (SIC) code is updated for the US company.',
    params: [],
    examples: [],
    notes: [
      'A SIC code change can be an early signal that a company is pivoting or changing direction.',
    ],
  },
];

// ─────────────────────────────────────────────
// SMALL COMPONENTS
// ─────────────────────────────────────────────

function CategoryBadge({ category }: { category: CategoryKey }) {
  const cat = CATEGORIES[category];
  const colors = COLOR_MAP[cat.color as ColorType];
  return (
    <span
      className={`inline-flex items-center gap-1 text-[11px] font-medium border rounded-full px-2 py-0.5 ${colors.badge}`}>
      {cat.icon} {cat.label}
    </span>
  );
}

// ─────────────────────────────────────────────
// PARAM EDITOR — used both inside card and in selected panel
// ─────────────────────────────────────────────

function ParamEditor({
  param,
  value,
  onChange,
}: {
  param: Param;
  value: string | number | boolean;
  onChange: (v: string | number | boolean) => void;
}) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-medium text-gray-700">{param.userLabel}</label>
      <p className="text-[11px] text-gray-400 leading-snug">{param.description}</p>
      <div className="mt-1">
        {param.type === 'select' && (
          <select
            value={typeof value === 'string' ? value : ''}
            onChange={(e) => onChange(e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 bg-white">
            {(param.options ?? []).map((o) => (
              <option key={o} value={o}>
                {o === '' ? 'No preference' : o}
              </option>
            ))}
          </select>
        )}
        {param.type === 'toggle' && (
          <div className="flex items-center gap-2">
            <DillToggle value={typeof value === 'boolean' ? value : false} onChange={onChange} />
            <span className="text-xs text-gray-500">{value ? 'Yes — notify me' : 'No'}</span>
          </div>
        )}
        {param.type === 'number' && (
          <input
            type="number"
            value={value === '' ? '' : typeof value === 'number' ? value : ''}
            onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
            placeholder={param.placeholder}
            className="w-full border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 bg-white placeholder-gray-300"
          />
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// EXAMPLES TABLE
// ─────────────────────────────────────────────

function ExamplesTable({ examples }: { examples: Example[] }) {
  if (!examples.length)
    return <p className="text-xs text-gray-400 italic">No examples available.</p>;
  const keys = Object.keys(examples[0]).filter((k) => k !== 'goal');
  return (
    <div className="overflow-hidden rounded-lg border border-gray-100 mt-1">
      <table className="w-full text-xs">
        <thead>
          <tr className="bg-gray-50">
            {keys.map((k) => (
              <th
                key={k}
                className="text-left px-3 py-2 text-gray-400 font-medium border-b border-gray-100 font-mono">
                {k}
              </th>
            ))}
            <th className="text-left px-3 py-2 text-gray-500 font-medium border-b border-gray-100">
              What happens
            </th>
          </tr>
        </thead>
        <tbody>
          {examples.map((ex, i) => (
            <tr
              key={i}
              className="border-b border-gray-50 last:border-0 hover:bg-gray-50 transition-colors">
              {keys.map((k) => (
                <td key={k} className="px-3 py-2 font-mono text-indigo-600">
                  {ex[k]}
                </td>
              ))}
              <td className="px-3 py-2 text-gray-600">{ex.goal}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─────────────────────────────────────────────
// RULE CARD — available rules list
// ─────────────────────────────────────────────

function RuleCard({
  rule,
  isAdded,
  onAdd,
}: {
  rule: Rule;
  isAdded: boolean;
  onAdd: (rule: Rule, paramValues: Record<string, string | number | boolean>) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [tab, setTab] = useState<'configure' | 'examples' | 'notes'>('configure');
  const [paramValues, setParamValues] = useState<Record<string, string | number | boolean>>(() => {
    const init: Record<string, string | number | boolean> = {};
    rule.params.forEach((p) => {
      init[p.key] = p.type === 'toggle' ? false : '';
    });
    return init;
  });

  const setParam = (key: string, val: string | number | boolean) => {
    setParamValues((prev) => ({ ...prev, [key]: val }));
  };

  return (
    <div
      className={`rounded-xl border transition-all duration-200 bg-white ${
        isAdded ? 'border-blue-200 bg-blue-50/30' : 'border-gray-100 hover:border-gray-200'
      }`}>
      {/* ── Card header ── */}
      <div className="flex items-start gap-3 p-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-2 flex-wrap mb-1">
            <span className="font-semibold text-sm text-gray-900">{rule.name}</span>
            <span className="text-gray-300 text-xs self-center hidden sm:block">·</span>
            <span className="text-gray-400 text-xs self-center hidden sm:block">
              {rule.subtitle}
            </span>
          </div>
          <p className="text-xs text-gray-500 leading-relaxed mb-2">{rule.userDescription}</p>
          <CategoryBadge category={rule.category} />
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 flex-shrink-0 ml-2">
          {isAdded ? (
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-lg px-2.5 py-1.5">
              <CheckCircle size={12} />
              Added
            </span>
          ) : rule.type === RULE_TYPES.PARAM ? (
            // Param rules: toggle expand to configure first
            <button
              type="button"
              onClick={() => setExpanded((e) => !e)}
              className={`inline-flex items-center gap-1.5 text-xs font-medium rounded-lg px-2.5 py-1.5 transition-colors border ${
                expanded
                  ? 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                  : 'bg-blue-600 text-white border-transparent hover:bg-blue-700'
              }`}>
              <Plus size={12} />
              {expanded ? 'Hide' : 'Configure & Add'}
              <ChevronDown
                size={12}
                className={`transition-transform ${expanded ? 'rotate-180' : ''}`}
              />
            </button>
          ) : (
            // isActive rules: add immediately
            <button
              type="button"
              onClick={() => onAdd(rule, {})}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg px-2.5 py-1.5 transition-colors">
              <Plus size={12} />
              Add Rule
            </button>
          )}
        </div>
      </div>

      {/* ── Expanded config for param rules ── */}
      {expanded && rule.type === RULE_TYPES.PARAM && (
        <div className="border-t border-gray-100 px-4 pb-4 pt-3">
          {/* Tabs */}
          <div className="flex gap-0.5 mb-4 bg-gray-100 rounded-lg p-0.5 w-fit">
            {(['configure', 'examples', 'notes'] as const).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTab(t)}
                className={`px-3 py-1 text-xs rounded-md font-medium capitalize transition-all ${
                  tab === t
                    ? 'bg-white text-gray-800 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}>
                {t === 'configure' ? '⚙ Configure' : t === 'examples' ? '📋 Examples' : '💡 Notes'}
              </button>
            ))}
          </div>

          {tab === 'configure' && (
            <div className="space-y-4">
              {rule.params.map((p) => (
                <ParamEditor
                  key={p.key}
                  param={p}
                  value={paramValues[p.key]}
                  onChange={(v) => setParam(p.key, v)}
                />
              ))}
              {!isAdded && (
                <button
                  type="button"
                  onClick={() => onAdd(rule, paramValues)}
                  className="mt-2 w-full inline-flex items-center justify-center gap-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg px-3 py-2 transition-colors">
                  <Plus size={13} />
                  Add Rule with These Settings
                </button>
              )}
            </div>
          )}
          {tab === 'examples' && <ExamplesTable examples={rule.examples} />}
          {tab === 'notes' && (
            <ul className="space-y-2">
              {rule.notes.map((n, i) => (
                <li key={i} className="flex gap-2 text-xs text-gray-500 leading-relaxed">
                  <span className="text-blue-400 flex-shrink-0 mt-0.5">→</span>
                  {n}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Simple isActive-only info */}
      {expanded && rule.type === RULE_TYPES.ACTIVE && (
        <div className="border-t border-gray-100 px-4 pb-3 pt-3">
          {rule.notes.length > 0 && (
            <ul className="space-y-1.5">
              {rule.notes.map((n, i) => (
                <li key={i} className="flex gap-2 text-xs text-gray-500 leading-relaxed">
                  <span className="text-blue-400 flex-shrink-0 mt-0.5">→</span>
                  {n}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// SELECTED RULE CHIP — compact summary row
// ─────────────────────────────────────────────

function SelectedRuleRow({
  addedRule,
  onRemove,
  onUpdateParam,
}: {
  addedRule: AddedRule;
  onRemove: () => void;
  onUpdateParam: (ruleId: number, key: string, val: string | number | boolean) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const { rule, paramValues } = addedRule;
  const cat = CATEGORIES[rule.category];
  const colors = COLOR_MAP[cat.color as ColorType];

  const hasParams = rule.type === RULE_TYPES.PARAM && rule.params.length > 0;
  const configuredParams = rule.params.filter((p) => {
    const v = paramValues[p.key];
    return v !== '' && v !== false;
  });

  return (
    <div className="border border-gray-100 rounded-xl bg-white overflow-hidden">
      <div className="flex items-center gap-3 px-3 py-2.5">
        {/* Colour dot */}
        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${colors.dot}`} />

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-800 truncate">{rule.name}</span>
            {hasParams && configuredParams.length > 0 && (
              <span className="text-[10px] text-gray-400 flex-shrink-0">
                {configuredParams.length} condition{configuredParams.length > 1 ? 's' : ''} set
              </span>
            )}
            {hasParams && configuredParams.length === 0 && (
              <span className="text-[10px] text-amber-500 flex-shrink-0 flex items-center gap-0.5">
                <Info size={10} /> No conditions set
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5 mt-0.5">
            <CategoryBadge category={rule.category} />
          </div>
        </div>

        <div className="flex items-center gap-1 flex-shrink-0">
          {hasParams && (
            <button
              type="button"
              onClick={() => setExpanded(!expanded)}
              className="text-gray-400 hover:text-gray-600 transition-colors text-[11px] flex items-center gap-0.5 px-2 py-1 rounded-lg hover:bg-gray-100">
              {expanded ? 'Done' : 'Edit'}
              <ChevronDown
                size={12}
                className={`transition-transform ${expanded ? 'rotate-180' : ''}`}
              />
            </button>
          )}
          <button
            type="button"
            onClick={onRemove}
            className="text-gray-300 hover:text-red-500 transition-colors p-1.5 rounded-lg hover:bg-red-50">
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      {/* Inline param summary */}
      {!expanded && hasParams && configuredParams.length > 0 && (
        <div className="px-3 pb-2.5 flex flex-wrap gap-1.5">
          {configuredParams.map((p) => (
            <span
              key={p.key}
              className="inline-flex items-center gap-1 text-[11px] text-gray-500 bg-gray-50 border border-gray-100 rounded-md px-2 py-0.5">
              <span className="text-gray-400">{p.userLabel.split(' ').slice(0, 4).join(' ')}…</span>
              <span className="font-mono text-indigo-600 font-medium">
                {typeof paramValues[p.key] === 'boolean'
                  ? paramValues[p.key]
                    ? 'Yes'
                    : 'No'
                  : String(paramValues[p.key])}
              </span>
            </span>
          ))}
        </div>
      )}

      {/* Expanded edit form */}
      {expanded && hasParams && (
        <div className="border-t border-gray-100 px-3 pb-3 pt-3 bg-gray-50 space-y-4">
          {rule.params.map((p) => (
            <ParamEditor
              key={p.key}
              param={p}
              value={paramValues[p.key]}
              onChange={(v) => {
                onUpdateParam(rule.id, p.key, v);
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// CATEGORY GROUP (available list)
// ─────────────────────────────────────────────

function CategoryGroup({
  catKey,
  rules,
  addedIds,
  onAdd,
}: {
  catKey: CategoryKey;
  rules: Rule[];
  addedIds: Set<number>;
  onAdd: (rule: Rule, paramValues: Record<string, string | number | boolean>) => void;
}) {
  const [collapsed, setCollapsed] = useState(false);
  const cat = CATEGORIES[catKey];
  const colors = COLOR_MAP[cat.color as ColorType];

  return (
    <div className="mb-3">
      <button
        type="button"
        onClick={() => setCollapsed(!collapsed)}
        className="flex items-center gap-2 mb-2 w-full text-left group">
        <span>{cat.icon}</span>
        <span className="text-xs font-semibold text-gray-600 uppercase tracking-widest">
          {cat.label}
        </span>
        <span className={`text-[10px] font-medium border rounded-full px-1.5 py-0 ${colors.badge}`}>
          {rules.length}
        </span>
        <div className="flex-1 h-px bg-gray-100" />
        <ChevronDown
          size={12}
          className={`text-gray-300 transition-transform ${collapsed ? '-rotate-90' : ''}`}
        />
      </button>
      {!collapsed && (
        <div className="space-y-2">
          {rules.map((r) => (
            <RuleCard key={r.id} rule={r} isAdded={addedIds.has(r.id)} onAdd={onAdd} />
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// ALL_CATEGORIES filter options
// ─────────────────────────────────────────────

const FILTER_CATEGORIES = [
  { value: 'all', label: 'All Categories' },
  ...Object.entries(CATEGORIES).map(([k, v]) => ({ value: k, label: `${v.icon} ${v.label}` })),
];

// ─────────────────────────────────────────────
// EXPORTED TYPES + DATA
// ─────────────────────────────────────────────

export type { AddedRule, Rule, Param, CategoryKey };
export { ALL_RULES };

// ─────────────────────────────────────────────
// MAIN MODAL
// ─────────────────────────────────────────────

const AddPortfolioRulesModal = ({
  open,
  handleClose,
  existingRules = [],
  onSave,
}: {
  open: boolean;
  handleClose: () => void;
  existingRules?: AddedRule[];
  onSave?: (rules: AddedRule[]) => void;
}) => {
  const [filterCat, setFilterCat] = useState('all');
  const [filterText, setFilterText] = useState('');
  // Initialise from existingRules every time the modal opens
  const [addedRules, setAddedRules] = useState<AddedRule[]>(existingRules);
  const [view, setView] = useState<'browse' | 'selected'>('browse');

  // Re-sync when existingRules changes (e.g. modal re-opened)
  const prevOpen = useState(open)[0];
  useMemo(() => {
    if (open) setAddedRules(existingRules);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const addedIds = useMemo(() => new Set(addedRules.map((ar) => ar.rule.id)), [addedRules]);

  const handleAdd = (rule: Rule, paramValues: Record<string, string | number | boolean>) => {
    if (addedIds.has(rule.id)) return;
    setAddedRules((prev) => [...prev, { rule, paramValues, isActive: true }]);
  };

  const handleRemove = (ruleId: number) => {
    setAddedRules((prev) => prev.filter((ar) => ar.rule.id !== ruleId));
  };

  const handleUpdateParam = (ruleId: number, key: string, val: string | number | boolean) => {
    setAddedRules((prev) =>
      prev.map((ar) =>
        ar.rule.id === ruleId ? { ...ar, paramValues: { ...ar.paramValues, [key]: val } } : ar
      )
    );
  };

  const handleCloseModal = () => {
    setFilterCat('all');
    setFilterText('');
    setView('browse');
    handleClose();
  };

  const handleSubmit = () => {
    onSave?.(addedRules);
    handleCloseModal();
  };

  // Filter and group available rules
  const filteredRules = useMemo(
    () =>
      ALL_RULES.filter((r) => {
        if (filterCat !== 'all' && r.category !== filterCat) return false;
        if (filterText) {
          const q = filterText.toLowerCase();
          return (
            r.name.toLowerCase().includes(q) ||
            r.subtitle.toLowerCase().includes(q) ||
            String(r.id).includes(q) ||
            r.userDescription.toLowerCase().includes(q)
          );
        }
        return true;
      }),
    [filterCat, filterText]
  );

  const groupedRules = useMemo(() => {
    const g: Partial<Record<CategoryKey, Rule[]>> = {};
    for (const r of filteredRules) {
      if (!g[r.category]) g[r.category] = [];
      g[r.category]!.push(r);
    }
    return g;
  }, [filteredRules]);

  const catOrder: CategoryKey[] = [
    'credit_score',
    'credit_limit',
    'payment',
    'company_info',
    'financials',
    'risk',
    'bankruptcy',
  ];

  return (
    <DillModal
      open={open}
      onClose={handleCloseModal}
      title="Add Monitoring Rules"
      subtitle="Choose what changes you want to be alerted about for companies in this portfolio."
      maxWidth="700px"
      contentClassName="flex-1 min-h-0 overflow-y-auto flex flex-col"
      footer={
        <>
          <DillButton label="Cancel" onClick={handleCloseModal} variant="secondary" />
          <div className="flex items-center gap-3">
            {addedRules.length > 0 && (
              <span className="text-xs text-gray-400">
                {addedRules.length} rule{addedRules.length > 1 ? 's' : ''} ready to save
              </span>
            )}
            <DillButton
              label={
                addedRules.length > 0
                  ? `Save ${addedRules.length} Rule${addedRules.length > 1 ? 's' : ''}`
                  : 'Save Rules'
              }
              onClick={handleSubmit}
              isDisabled={addedRules.length === 0}
            />
          </div>
        </>
      }>
      {/* ── Tab strip + Added count ── */}
      <div className="flex items-center gap-2 px-5 pt-3 pb-0 flex-shrink-0">
        <div className="flex gap-0.5 bg-gray-100 rounded-lg p-0.5">
          <button
            type="button"
            onClick={() => setView('browse')}
            className={`px-3 py-1.5 text-xs rounded-md font-medium transition-all ${
              view === 'browse'
                ? 'bg-white text-gray-800 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}>
            Browse Rules
          </button>
          <button
            type="button"
            onClick={() => setView('selected')}
            className={`px-3 py-1.5 text-xs rounded-md font-medium transition-all flex items-center gap-1.5 ${
              view === 'selected'
                ? 'bg-white text-gray-800 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}>
            Selected
            {addedRules.length > 0 && (
              <span
                className={`text-[10px] font-bold rounded-full px-1.5 min-w-[18px] text-center ${
                  view === 'selected' ? 'bg-blue-600 text-white' : 'bg-blue-100 text-blue-600'
                }`}>
                {addedRules.length}
              </span>
            )}
          </button>
        </div>

        {/* Quick summary when browsing */}
        {view === 'browse' && addedRules.length > 0 && (
          <span className="text-[11px] text-gray-400 ml-1">
            {addedRules.length} rule{addedRules.length > 1 ? 's' : ''} selected
          </span>
        )}
      </div>

      {/* ── Browse view ── */}
      {view === 'browse' && (
        <>
          {/* Filter bar */}
          <div className="px-5 py-3 flex gap-2 flex-shrink-0">
            <div className="relative flex-1">
              <svg
                className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-300"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <input
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                placeholder="Search rules…"
                className="w-full border border-gray-200 rounded-lg pl-8 pr-3 py-2 text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 placeholder-gray-300"
              />
            </div>
            <select
              value={filterCat}
              onChange={(e) => setFilterCat(e.target.value)}
              className="border border-gray-200 rounded-lg px-2.5 py-2 text-xs text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 bg-white">
              {FILTER_CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>

          {/* Rules list */}
          <div className="flex-1 overflow-y-auto px-5 pb-4">
            {filteredRules.length === 0 ? (
              <div className="text-center py-12 text-gray-400 text-sm">
                No rules match your search.
              </div>
            ) : (
              catOrder
                .filter((c) => groupedRules[c]?.length)
                .map((c) => (
                  <CategoryGroup
                    key={c}
                    catKey={c}
                    rules={groupedRules[c]!}
                    addedIds={addedIds}
                    onAdd={handleAdd}
                  />
                ))
            )}
          </div>
        </>
      )}

      {/* ── Selected view ── */}
      {view === 'selected' && (
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {addedRules.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="text-4xl mb-3">📋</div>
              <p className="text-sm font-medium text-gray-500">No rules added yet</p>
              <p className="text-xs text-gray-400 mt-1 max-w-xs">
                Go to Browse Rules and click "Add Rule" to start building your monitoring setup.
              </p>
              <button
                type="button"
                onClick={() => setView('browse')}
                className="mt-4 text-xs text-blue-600 hover:text-blue-700 font-medium underline underline-offset-2">
                Browse available rules →
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-xs text-gray-400 mb-3">
                {addedRules.length} rule{addedRules.length > 1 ? 's' : ''} will be enabled for this
                portfolio. You can adjust settings or remove rules before saving.
              </p>
              {addedRules.map((ar) => (
                <SelectedRuleRow
                  key={ar.rule.id}
                  addedRule={ar}
                  onRemove={() => handleRemove(ar.rule.id)}
                  onUpdateParam={handleUpdateParam}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </DillModal>
  );
};

export default AddPortfolioRulesModal;
